import { useMemo } from 'react';
import { formatCurrency } from '~/utils';
import {numberFormat} from "../../../utils";
import '../style/SuggestMoney.scss';

/**
 * Vietnamese currency denominations (sorted in ascending order)
 */
const DENOMINATIONS = [1000, 2000, 5000, 10000, 20000, 50000, 100000, 200000, 500000, 1000000];

/**
 * Generate money suggestions based on required amount
 * @param {number} requiredAmount - Amount customer needs to pay
 * @returns {number[]} Array of suggested amounts
 */
const generateMoneySuggestions = (requiredAmount) =>
{
    if (!requiredAmount || requiredAmount <= 0)
    {
        return DENOMINATIONS.slice(0, 6); // Return first 6 denominations
    }

    const suggestions = new Set();

    // 1. Add exact denominations >= required amount
    const baseIndex = DENOMINATIONS.findIndex(d => d >= requiredAmount);
    if (baseIndex !== -1) {
        // Add 2-3 closest denominations
        for (let i = 0; i < 3 && baseIndex + i < DENOMINATIONS.length; i++) {
            suggestions.add(DENOMINATIONS[baseIndex + i]);
        }
    } else {
        // If required > all denominations, add largest
        suggestions.add(DENOMINATIONS[DENOMINATIONS.length - 1]);
    }

    // 2. Smart rounding based on amount range - MORE COMPREHENSIVE
    if (requiredAmount < 10000) {
        // Under 10k: round to 5k, 10k, 20k
        const round5k = Math.ceil(requiredAmount / 5000) * 5000;
        const round10k = Math.ceil(requiredAmount / 10000) * 10000;
        suggestions.add(round5k);
        suggestions.add(round10k);
        suggestions.add(20000);
    }
    else if (requiredAmount < 50000) {
        // 10k-50k: round to 10k, 20k, 50k
        const round10k = Math.ceil(requiredAmount / 10000) * 10000;
        const round20k = Math.ceil(requiredAmount / 20000) * 20000;
        suggestions.add(round10k);
        suggestions.add(round20k);
        suggestions.add(50000);
    }
    else if (requiredAmount < 100000) {
        // 50k-100k: round to 10k, 20k, 50k, 100k
        const round10k = Math.ceil(requiredAmount / 10000) * 10000;
        const round20k = Math.ceil(requiredAmount / 20000) * 20000;
        const round50k = Math.ceil(requiredAmount / 50000) * 50000;
        suggestions.add(round10k);
        suggestions.add(round20k);
        suggestions.add(round50k);
        suggestions.add(100000);
    }
    else if (requiredAmount < 500000) {
        // 100k-500k: round to 10k, 50k, 100k, 200k, 500k
        const round10k = Math.ceil(requiredAmount / 10000) * 10000;
        const round50k = Math.ceil(requiredAmount / 50000) * 50000;
        const round100k = Math.ceil(requiredAmount / 100000) * 100000;
        const round200k = Math.ceil(requiredAmount / 200000) * 200000;
        suggestions.add(round10k);
        suggestions.add(round50k);
        suggestions.add(round100k);
        if (round200k !== 500000) suggestions.add(round200k);
        suggestions.add(500000);
    }
    else if (requiredAmount < 1000000) {
        // 500k-1M: round to 10k, 50k, 100k, 200k, 500k, 1M
        const round10k = Math.ceil(requiredAmount / 10000) * 10000;
        const round50k = Math.ceil(requiredAmount / 50000) * 50000;
        const round100k = Math.ceil(requiredAmount / 100000) * 100000;
        const round200k = Math.ceil(requiredAmount / 200000) * 200000;
        const round500k = Math.ceil(requiredAmount / 500000) * 500000;
        suggestions.add(round10k);
        suggestions.add(round50k);
        suggestions.add(round100k);
        if (round200k !== round500k) suggestions.add(round200k);
        if (round500k !== 1000000) suggestions.add(round500k);
        suggestions.add(1000000);
    }
    else {
        // >= 1M: round to 50k, 100k, 200k, 500k, 1M
        const round50k = Math.ceil(requiredAmount / 50000) * 50000;
        const round100k = Math.ceil(requiredAmount / 100000) * 100000;
        const round200k = Math.ceil(requiredAmount / 200000) * 200000;
        const round500k = Math.ceil(requiredAmount / 500000) * 500000;
        const round1M = Math.ceil(requiredAmount / 1000000) * 1000000;
        suggestions.add(round50k);
        suggestions.add(round100k);
        if (round200k !== round500k) suggestions.add(round200k);
        if (round500k !== round1M) suggestions.add(round500k);
        suggestions.add(round1M);
    }

    // 3. Filter out values < required amount and sort
    const validSuggestions = Array.from(suggestions)
        .filter(amount => amount >= requiredAmount)
        .sort((a, b) => a - b);

    // Return max 8 suggestions (increased from 6 for more coverage)
    return validSuggestions.slice(0, 8);
};

const SuggestMoney = ({ requiredAmount, onSelect }) => {
    const suggestions = useMemo(
        () => generateMoneySuggestions(requiredAmount),
        [requiredAmount]
    );

    if (!suggestions || suggestions.length === 0) {
        return null;
    }

    return (
        <div className="suggest-money">
            <div className="suggest-money-items">
                {suggestions.map((amount, index) => (
                    <button
                        key={index}
                        className="suggest-money-btn"
                        onClick={() => onSelect(amount)}
                        title={formatCurrency(amount)}
                    >
                        {numberFormat((amount >= 1000000)
                            ? (amount).toFixed(0)
                            : amount >= 100000
                            ? (amount).toFixed(0)
                            : (amount).toFixed(1))
                        }
                    </button>
                ))}
            </div>
        </div>
    );
};

export default SuggestMoney;
